/**
 * #4918 — events (webinars) for the public /events and /events/:slug pages.
 * Pure helpers (no React, no fetch) so they are unit-tested in node.
 *
 * Data: cms-content-worker `/api/events` through the www-sbi `/api/content/*` proxy
 * (→ cms `/api/events`). Public reads return published and cancelled events only.
 * The page links to the Eventbrite registration only; it never shows a meeting join
 * link (the collection has no join-link field).
 */

export type EventStatus = 'draft' | 'published' | 'cancelled';

export interface SiteEvent {
  id: number;
  title: string;
  slug: string;
  starts_at: string;
  ends_at?: string | null;
  timezone?: string | null;
  status: EventStatus;
  eventbrite_url?: string | null;
  summary?: string | null;
  content?: string | null;
  deck_url?: string | null;
  recording_url?: string | null;
  /** JSON array string of { title, url } */
  resources?: string | null;
  series?: string | null;
  meta_title?: string | null;
  meta_description?: string | null;
  meta_image_url?: string | null;
}

export interface EventResource {
  title: string;
  url: string;
}

export const EVENTS_API = '/api/content/events';
const DEFAULT_TZ = 'America/Los_Angeles';

/** An event is upcoming until it ends (or until it starts, when it has no end). */
export function isUpcoming(event: Pick<SiteEvent, 'starts_at' | 'ends_at'>, now: Date = new Date()): boolean {
  const end = Date.parse(event.ends_at || event.starts_at);
  return Number.isFinite(end) && end >= now.getTime();
}

/** Split into upcoming (soonest first) and past (most recent first). */
export function splitEvents(events: SiteEvent[], now: Date = new Date()): { upcoming: SiteEvent[]; past: SiteEvent[] } {
  const upcoming = events.filter((e) => isUpcoming(e, now)).sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at));
  const past = events.filter((e) => !isUpcoming(e, now)).sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at));
  return { upcoming, past };
}

/** Parse the stored resources JSON; keeps only https entries with a title. Never throws. */
export function parseResources(raw: string | null | undefined): EventResource[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (r): r is EventResource =>
        typeof r?.title === 'string' && r.title.trim().length > 0 && isSafeHttpsUrl(r?.url)
    );
  } catch {
    return [];
  }
}

/** True for an https URL (the page renders only these as links). */
export function isSafeHttpsUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * "Tue, Oct 13, 2026 · 11:00 AM – 12:00 PM PDT" in the event's time zone.
 * Falls back to the default zone when the stored one is not a valid IANA zone.
 */
export function formatEventWhen(event: Pick<SiteEvent, 'starts_at' | 'ends_at' | 'timezone'>): string {
  const timeZone = validTimeZone(event.timezone) ?? DEFAULT_TZ;
  const start = new Date(event.starts_at);
  if (Number.isNaN(start.getTime())) return '';
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(start);
  const time = new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' });
  const zone = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(start)
    .find((p) => p.type === 'timeZoneName')?.value;
  const end = event.ends_at ? new Date(event.ends_at) : null;
  const range =
    end && !Number.isNaN(end.getTime()) ? `${time.format(start)} – ${time.format(end)}` : time.format(start);
  return `${day} · ${range}${zone ? ` ${zone}` : ''}`;
}

function validTimeZone(tz: string | null | undefined): string | null {
  if (!tz) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return null;
  }
}

/** The slug from a /events/:slug path, or undefined. */
export function slugFromEventsPath(pathname: string): string | undefined {
  const m = /^\/events\/([^/?#]+)\/?$/.exec(pathname);
  if (!m) return undefined;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    // A malformed escape such as %E0 is not a slug: show the 404, not the error page.
    return undefined;
  }
}
